import { SuspenseSelect } from '@/components/Suspense';
import {
  BaseSelectProps,
  useSetCacheQueryData,
} from '@/context/SelectComponent/Base';
import { useModelsToOptionsWithCacheItems } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { getPageData } from '@/utils/getPageData';
import {
  FormSelect,
  FormSelectProps,
  SelectProps,
} from '@moneyforward/ap-frontend-components';
import {
  CrItems as CrItemsModel,
  useGetCrItemsSuspenseInfinite,
} from 'ap-openapi';
import { memo, startTransition, useCallback, useMemo, useState } from 'react';
import {
  FieldPath,
  FieldValues,
  Path,
  PathValue,
  useFormContext,
} from 'react-hook-form';

export type Props<TFieldValues extends FieldValues> = {
  selectProps?: Omit<
    FormSelectProps<TFieldValues>['selectProps'],
    'options' | 'comboBox'
  >;
  query?: string;
  per?: number;
  validateMaxCount?: number;
  comboBox?: boolean;
  clearCrSubItemName?: FieldPath<TFieldValues>;
  setLabelField?: (label: string) => void;
} & Omit<FormSelectProps<TFieldValues>, 'selectProps' | 'label'> &
  BaseSelectProps;

export const _CrItemsSelect = <TFieldValues extends FieldValues>({
  selectProps: _selectProps,
  defaultOptions: _defaultOptions,
  onChangeAfter,
  setLabelField,
  clearCrSubItemName,
  cacheQueryKey,
  per = 25,
  comboBox = true,
  ...rest
}: Props<TFieldValues>) => {
  const defaultOptions = useMemo(
    () => _defaultOptions ?? [],
    [_defaultOptions]
  );
  const { t } = useTranslation();
  const [query, setQuery] = useState<string | undefined>(undefined);
  const {
    data: apiData,
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetCrItemsSuspenseInfinite(
    {
      query,
      per: per,
    },
    {
      query: {
        getNextPageParam: (page) => {
          const url = new URL(page.data.next ?? '/', location.href);
          if (!url.searchParams.has('page')) return undefined;
          return Number(url.searchParams.get('page'));
        },
      },
    }
  );
  if (error) throw error;
  const data = useMemo(() => getPageData(apiData, 'cr_items'), [apiData]);
  const options = useModelsToOptionsWithCacheItems<CrItemsModel>(
    data,
    cacheQueryKey,
    query,
    defaultOptions
  );
  const fetchMore = useCallback(
    async (callback: () => void) => {
      if (hasNextPage) {
        await fetchNextPage();
      }
      callback();
    },
    [fetchNextPage, hasNextPage]
  );
  const onSearch = useCallback((value: string) => {
    startTransition(() => {
      setQuery(value);
    });
  }, []);
  const selectProps: SelectProps = useMemo(
    () => ({
      onSearchDebounceWait: 600,
      comboBox: comboBox,
      placeholder: t('cr_items_placeholder'),
      inputPlaceholder: t('cr_items_input_placeholder'),
      ..._selectProps,
      options: options,
      onSearch: onSearch,
      loading: isLoading,
      mode: 'single',
      fetchMore: fetchMore,
      selectAll: undefined,
      selectAllLabel: undefined,
    }),
    [_selectProps, comboBox, fetchMore, isLoading, onSearch, options, t]
  );
  const { setValue } = useFormContext<TFieldValues>();
  const wrapOnChangeAfter = useCallback(
    (e: string | number | (string | number)[]) => {
      const selectedOption = options.find(
        (option) => option.value === String(e)
      );
      setLabelField?.(selectedOption?.label ?? '');

      onChangeAfter?.(e);
      if (clearCrSubItemName)
        setValue(
          clearCrSubItemName,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          null as PathValue<TFieldValues, Path<TFieldValues>>
        );
    },
    [clearCrSubItemName, onChangeAfter, setLabelField, options, setValue]
  );

  useSetCacheQueryData(cacheQueryKey, data);

  return (
    <FormSelect
      selectProps={selectProps}
      onChangeAfter={wrapOnChangeAfter}
      {...rest}
    />
  );
};
_CrItemsSelect.displayName = '_CrItemsSelect';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const CrItemsSelect = memo((props) => (
  <SuspenseSelect>
    <_CrItemsSelect {...props} />
  </SuspenseSelect>
)) as typeof _CrItemsSelect;
CrItemsSelect.displayName = 'CrItemsSelect';
